import type { ClienteCardapio } from "./types";
import type { ModoAtendimento } from "../atendimentoMode";
import { normalizeMesaDigits } from "./formFields";

const CLIENT_KEY = "cardapio_cliente_v1";
const MESA_SESSAO_KEY = "cardapio_mesa_sessao_v1";
const SESSAO_COMANDA_KEY = "cardapio_sessao_comanda_ativa_v1";
const ATENDIMENTO_VINCULO_KEY = "cardapio_atendimento_vinculo_v1";

function scopedKey(base: string, scope?: string): string {
  const safeScope = (scope ?? "").trim();
  return safeScope ? `${base}:${safeScope}` : base;
}

export type CardapioAtendimentoVinculo = {
  comanda_id: number;
  nome: string;
  mesa: string;
};

export function loadCliente(scope?: string): ClienteCardapio | null {
  try {
    const raw = localStorage.getItem(scopedKey(CLIENT_KEY, scope));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ClienteCardapio>;
    if (!parsed.nome?.trim()) return null;
    return { nome: parsed.nome.trim(), whatsapp: parsed.whatsapp?.trim() ?? "" };
  } catch {
    return null;
  }
}

export function saveCliente(cliente: ClienteCardapio, scope?: string): void {
  localStorage.setItem(
    scopedKey(CLIENT_KEY, scope),
    JSON.stringify({ nome: cliente.nome.trim(), whatsapp: cliente.whatsapp.trim() }),
  );
}

export function clearCliente(scope?: string): void {
  localStorage.removeItem(scopedKey(CLIENT_KEY, scope));
}

export function loadMesaSessao(modo: ModoAtendimento = "mesa", scope?: string): string {
  try {
    const raw = localStorage.getItem(scopedKey(MESA_SESSAO_KEY, scope));
    if (!raw) return "";
    return normalizeMesaDigits(raw, modo);
  } catch {
    return "";
  }
}

export function saveMesaSessao(mesa: string, modo: ModoAtendimento = "mesa", scope?: string): void {
  const normalized = normalizeMesaDigits(mesa, modo);
  const key = scopedKey(MESA_SESSAO_KEY, scope);
  if (normalized) {
    localStorage.setItem(key, normalized);
  } else {
    localStorage.removeItem(key);
  }
}

export function clearMesaSessao(scope?: string): void {
  localStorage.removeItem(scopedKey(MESA_SESSAO_KEY, scope));
}

export function loadAtendimentoVinculo(modo: ModoAtendimento = "mesa", scope?: string): CardapioAtendimentoVinculo | null {
  try {
    const raw = localStorage.getItem(scopedKey(ATENDIMENTO_VINCULO_KEY, scope));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CardapioAtendimentoVinculo>;
    const comandaId = Number(parsed.comanda_id);
    if (!Number.isFinite(comandaId) || comandaId <= 0) return null;
    return {
      comanda_id: comandaId,
      nome: parsed.nome?.trim() ?? "",
      mesa: normalizeMesaDigits(parsed.mesa ?? "", modo),
    };
  } catch {
    return null;
  }
}

export function saveAtendimentoVinculo(
  vinculo: CardapioAtendimentoVinculo,
  modo: ModoAtendimento = "mesa",
  scope?: string,
): void {
  if (!Number.isFinite(vinculo.comanda_id) || vinculo.comanda_id <= 0) return;
  localStorage.setItem(
    scopedKey(ATENDIMENTO_VINCULO_KEY, scope),
    JSON.stringify({
      comanda_id: vinculo.comanda_id,
      nome: vinculo.nome.trim(),
      mesa: normalizeMesaDigits(vinculo.mesa, modo),
    }),
  );
}

export function clearAtendimentoVinculo(scope?: string): void {
  localStorage.removeItem(scopedKey(ATENDIMENTO_VINCULO_KEY, scope));
}

export function markSessaoComandaAtiva(scope?: string): void {
  localStorage.setItem(scopedKey(SESSAO_COMANDA_KEY, scope), "1");
}

export function hasSessaoComandaAtiva(scope?: string): boolean {
  return localStorage.getItem(scopedKey(SESSAO_COMANDA_KEY, scope)) === "1";
}

export function clearCardapioSessao(scope?: string): void {
  clearCliente(scope);
  clearMesaSessao(scope);
  clearAtendimentoVinculo(scope);
  localStorage.removeItem(scopedKey(SESSAO_COMANDA_KEY, scope));
}
